import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, Clock, MessageSquare, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const toAbsUrl = (url) => url && !url.startsWith("http") ? `${BACKEND_URL}${url}` : url;
const ROLE_LABEL = { admin: "Admin", coach: "Coach", mentor: "Mentor" };

export default function GradesPage() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/submissions").then(r => {
      setSubmissions(r.data);
    }).finally(() => setLoading(false));
  }, []);

  const handleDelete = async (s) => {
    if (!window.confirm("Hapus jawaban ini?")) return;
    try {
      await api.delete(`/me/submissions/${s.submission_id}`);
      setSubmissions(prev => prev.filter(x => x.submission_id !== s.submission_id));
      toast.success("Jawaban dihapus");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus");
    }
  };

  const graded = submissions.filter(s => s.grade != null);
  const pending = submissions.filter(s => s.grade == null);
  const avg = graded.length ? Math.round(graded.reduce((s, x) => s + x.grade, 0) / graded.length) : null;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <Link to="/dashboard" className="flex items-center gap-2 text-sm text-[#5C5868] hover:text-[#1A1422] mb-6">
        <ArrowLeft className="w-4 h-4" /> Dashboard
      </Link>

      <h1 className="font-heading text-3xl font-extrabold text-[#1A1422] mb-2">Nilai Saya</h1>
      <p className="text-[#5C5868] mb-8">Riwayat tugas, nilai, dan feedback dari coach & mentor.</p>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="card-surface p-5 text-center">
          <div className="text-2xl font-heading font-extrabold text-[#1A1422]">{submissions.length}</div>
          <div className="text-xs text-[#8B8794] mt-1">Total tugas</div>
        </div>
        <div className="card-surface p-5 text-center">
          <div className="text-2xl font-heading font-extrabold text-[#10B981]">{graded.length}</div>
          <div className="text-xs text-[#8B8794] mt-1">Sudah dinilai</div>
        </div>
        <div className="card-surface p-5 text-center">
          <div className="text-2xl font-heading font-extrabold text-[#FF8C00]">{avg ?? "—"}</div>
          <div className="text-xs text-[#8B8794] mt-1">Rata-rata nilai</div>
        </div>
      </div>

      {loading ? (
        <div className="text-[#8B8794] text-sm">Memuat...</div>
      ) : submissions.length === 0 ? (
        <div className="card-surface p-10 text-center text-[#8B8794]">Belum ada tugas yang dikumpulkan.</div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Pending */}
          {pending.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-[#8B8794] uppercase tracking-wide mb-3">Menunggu penilaian ({pending.length})</h2>
              <div className="flex flex-col gap-3">
                {pending.map(s => (
                  <div key={s.submission_id} className="card-surface p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[#1A1422] mb-1">{s.lesson_title}</div>
                        <div className="text-xs text-[#8B8794]">
                          Dikumpulkan {new Date(s.submitted_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs bg-[#F59E0B]/10 text-[#F59E0B] px-3 py-1 rounded-full font-semibold flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Menunggu
                        </span>
                        <button
                          onClick={() => handleDelete(s)}
                          className="flex items-center gap-1 text-xs text-[#E24B4A] hover:underline px-2 py-1 rounded hover:bg-[#FCEBEB]"
                          title="Hapus jawaban"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Hapus
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Graded */}
          {graded.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-[#8B8794] uppercase tracking-wide mb-3">Sudah dinilai ({graded.length})</h2>
              <div className="flex flex-col gap-3">
                {graded.map(s => (
                  <div key={s.submission_id} className="card-surface p-5">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <div className="font-semibold text-[#1A1422] mb-1">{s.lesson_title}</div>
                        <div className="text-xs text-[#8B8794]">
                          Dikumpulkan {new Date(s.submitted_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-lg font-heading font-extrabold ${s.grade >= 80 ? "text-[#10B981]" : s.grade >= 60 ? "text-[#378ADD]" : "text-[#F59E0B]"}`}>
                          {s.grade}
                        </span>
                        <span className="text-xs text-[#8B8794]">/100</span>
                        <CheckCircle className="w-4 h-4 text-[#10B981]" />
                      </div>
                    </div>

                    {/* Nilai bar */}
                    <div className="h-1.5 bg-[#F4F2F8] rounded-full overflow-hidden mb-3">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${s.grade}%`, background: s.grade >= 80 ? "#10B981" : s.grade >= 60 ? "#378ADD" : "#F59E0B" }}
                      />
                    </div>

                    {/* Feedback */}
                    {s.feedback && (
                      <div className="bg-[#F4F2F8] rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <MessageSquare className="w-3.5 h-3.5 text-[#6B21A8]" />
                          <span className="text-xs font-semibold text-[#5C5868]">Feedback</span>
                          {s.graded_by_picture && (
                            <img src={toAbsUrl(s.graded_by_picture)} alt="" className="w-5 h-5 rounded-full object-cover ml-1" />
                          )}
                          <span className="text-xs text-[#8B8794]">{s.graded_by_name || "BisaUsaha"}</span>
                          {s.graded_by_role && (
                            <span className="text-[10px] bg-[#6B21A8]/10 text-[#6B21A8] px-2 py-0.5 rounded-full">
                              {ROLE_LABEL[s.graded_by_role] || s.graded_by_role}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#1A1422] italic">"{s.feedback}"</p>
                        {s.graded_at && (
                          <div className="text-[10px] text-[#8B8794] mt-1">
                            {new Date(s.graded_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
